// ThreeJsRibbon.jsx - VANILLA THREE.JS with DYNAMIC CONSTELLATION UPDATES
import { useRef, useEffect, useState, useCallback, lazy, Suspense } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { Maximize2, Minimize2, RotateCcw, Globe, Target, Layers, Printer, Download, Loader2, Check, AlertCircle } from 'lucide-react';

const AladinOverlay = lazy(() => import('./AladinOverlay'));

// ============== PROJECTION FUNCTIONS ==============

/**
 * GNOMONIC projection - accurate for narrow fields (<60° FOV)
 * Projects onto a flat tangent plane at the zenith point.
 */
function projectGnomonic(ra, dec, ra0, dec0) {
  const toRad = Math.PI / 180;
  
  const ra_rad = ra * toRad;
  const dec_rad = dec * toRad;
  const ra0_rad = ra0 * toRad;
  const dec0_rad = dec0 * toRad;
  
  const cosDec = Math.cos(dec_rad);
  const cosDec0 = Math.cos(dec0_rad);
  const sinDec = Math.sin(dec_rad);
  const sinDec0 = Math.sin(dec0_rad);
  const cosDeltaRa = Math.cos(ra_rad - ra0_rad);
  
  const denom = sinDec0 * sinDec + cosDec0 * cosDec * cosDeltaRa;
  
  if (denom < 0.01) return null;
  
  const x = (cosDec * Math.sin(ra_rad - ra0_rad)) / denom;
  const y = (cosDec0 * sinDec - sinDec0 * cosDec * cosDeltaRa) / denom;
  
  return {
    x: x * 57.2958 * 60,
    y: y * 57.2958 * 60
  };
}

/**
 * EQUIRECTANGULAR projection - simple linear mapping for full-sky
 */
function projectEquirectangular(ra, dec, ra0, dec0) {
  let deltaRa = ra - ra0;
  if (deltaRa > 180) deltaRa -= 360;
  if (deltaRa < -180) deltaRa += 360;
  
  const deltaDec = dec - dec0;
  
  return {
    x: deltaRa * 60,
    y: deltaDec * 60
  };
}

/**
 * HYBRID projection - switches based on field of view
 */
function projectHybrid(ra, dec, ra0, dec0, fieldOfViewDeg) {
  if (fieldOfViewDeg < 60) {
    const result = projectGnomonic(ra, dec, ra0, dec0);
    if (result) return result;
  }
  return projectEquirectangular(ra, dec, ra0, dec0);
}

// Spectral color from temperature
function getSpectralColor(teff) {
  if (!teff || teff <= 0) return '#FFFFFF';
  if (teff >= 30000) return '#9BB0FF';
  if (teff >= 10000) return '#AABFFF';
  if (teff >= 7500) return '#CAD7FF';
  if (teff >= 6000) return '#F8F7FF';
  if (teff >= 5200) return '#FFF4EA';
  if (teff >= 3700) return '#FFD2A1';
  return '#FFCC6F';
}

// Curated sky surveys available via CDS hips2fits
const SKY_SURVEYS = [
  { id: 'CDS/P/DSS2/color', label: 'DSS2 Color', desc: 'Digitized Sky Survey (optical)' },
  { id: 'CDS/P/PanSTARRS/DR1/color-z-zg-g', label: 'PanSTARRS', desc: 'Pan-STARRS DR1 (optical)' },
  { id: 'CDS/P/2MASS/color', label: '2MASS', desc: 'Two Micron All Sky Survey (infrared)' },
  { id: 'CDS/P/allWISE/color', label: 'WISE', desc: 'Wide-field Infrared Survey (mid-IR)' },
  { id: 'CDS/P/GALEXGR6/AIS/color', label: 'GALEX', desc: 'Galaxy Evolution Explorer (UV)' },
  { id: 'CDS/P/Fermi/color', label: 'Fermi', desc: 'Fermi LAT (gamma ray)' },
  { id: 'CDS/P/Mellinger/color', label: 'Mellinger', desc: 'Mellinger optical survey' },
  { id: 'CDS/P/DSS2/red', label: 'DSS2 Red', desc: 'Digitized Sky Survey (red channel)' },
  { id: 'CDS/P/DSS2/blue', label: 'DSS2 Blue', desc: 'Digitized Sky Survey (blue channel)' },
];

// Print presets for high-res image generation
const PRINT_PRESETS = [
  { value: 'small',  label: '12"×2.4"',  desc: 'Small print' },
  { value: 'medium', label: '24"×4.8"',  desc: 'Medium print' },
  { value: 'large',  label: '36"×7.2"',  desc: 'Large print' },
  { value: 'poster', label: '48"×9.6"',  desc: 'Poster' },
  { value: 'banner', label: '60"×12"',   desc: 'Banner' },
];

export default function ThreeJsRibbon({
  stars = [],
  zenithRa,
  zenithDec,
  ribbonBounds,
  constellations = [],
  bandId,
  onGeneratePrintImage,
  printImageStatus = {}
}) {
  const containerRef = useRef();
  const sceneRef = useRef();
  const cameraRef = useRef();
  const rendererRef = useRef();
  const controlsRef = useRef();
  const constellationGroupRef = useRef();
  const starFieldRef = useRef();
  const lastFovRef = useRef(12);
  
  // Store raw data for re-projection
  const rawDataRef = useRef({ stars: [], constellations: [], zenithRa: 0, zenithDec: 0 });
  
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [viewMode, setViewMode] = useState('ribbon');
  const [currentFov, setCurrentFov] = useState(12);

  // Sky survey overlay state
  const overlayMeshRef = useRef(null);
  const overlayTextureRef = useRef(null);
  const [overlayMode, setOverlayMode] = useState('off');
  const [surveyId, setSurveyId] = useState('CDS/P/DSS2/color');
  const [overlayOpacity, setOverlayOpacity] = useState(0.6);
  const [overlayLoading, setOverlayLoading] = useState(false);
  const [showOverlayPanel, setShowOverlayPanel] = useState(false);

  // Print quality state
  const [printPreset, setPrintPreset] = useState('medium');

  const handleReset = useCallback(() => {
    if (controlsRef.current) {
      controlsRef.current.reset();
    }
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  }, []);

  const toggleViewMode = useCallback(() => {
    setViewMode(prev => prev === 'ribbon' ? 'fullsky' : 'ribbon');
  }, []);

  // Function to rebuild constellation geometry with new projection
  const rebuildConstellations = useCallback((scene, fovDeg, ra0, dec0, constellationData) => {
    // Remove old constellation group
    if (constellationGroupRef.current) {
      scene.remove(constellationGroupRef.current);
      constellationGroupRef.current.traverse((obj) => {
        if (obj.geometry) obj.geometry.dispose();
        if (obj.material) obj.material.dispose();
      });
    }
    
    if (!constellationData || constellationData.length === 0) return;
    
    const constellationGroup = new THREE.Group();
    constellationGroupRef.current = constellationGroup;
    
    const constMat = new THREE.LineBasicMaterial({ 
      color: 0x00ff88, 
      opacity: 0.5, 
      transparent: true,
      linewidth: 1
    });
    
    constellationData.forEach(constellation => {
      const { coordinates, geometry_type } = constellation;
      const polygons = geometry_type === 'MultiPolygon' ? coordinates : [coordinates];
      
      polygons.forEach(polygon => {
        polygon.forEach(ring => {
          const linePoints = [];
          
          ring.forEach(coord => {
            const [ra, dec] = coord;
            const proj = projectHybrid(ra, dec, ra0, dec0, fovDeg);
            if (proj) {
              linePoints.push(new THREE.Vector3(proj.x, proj.y, 0.5));
            }
          });
          
          if (linePoints.length > 1) {
            const lineGeo = new THREE.BufferGeometry().setFromPoints(linePoints);
            const line = new THREE.Line(lineGeo, constMat);
            constellationGroup.add(line);
          }
        });
      });
    });
    
    scene.add(constellationGroup);
  }, []);

  // Function to rebuild stars with new projection
  const rebuildStars = useCallback((scene, fovDeg, ra0, dec0, starData) => {
    // Remove old star field
    if (starFieldRef.current) {
      scene.remove(starFieldRef.current);
      if (starFieldRef.current.geometry) starFieldRef.current.geometry.dispose();
      if (starFieldRef.current.material) starFieldRef.current.material.dispose();
    }
    
    if (!starData || starData.length === 0) return { minX: 0, maxX: 0, minY: 0, maxY: 0 };
    
    const positions = new Float32Array(starData.length * 3);
    const colors = new Float32Array(starData.length * 3);
    const sizes = new Float32Array(starData.length);
    const color = new THREE.Color();

    const mags = starData.map(s => s.magnitude || 15).filter(m => m > 0 && m < 25);
    const magMin = Math.min(...mags);

    let minX = Infinity, maxX = -Infinity;
    let minY = Infinity, maxY = -Infinity;

    starData.forEach((star, i) => {
      const proj = projectHybrid(star.ra, star.dec, ra0, dec0, fovDeg);
      
      if (proj) {
        positions[i * 3] = proj.x;
        positions[i * 3 + 1] = proj.y;
        positions[i * 3 + 2] = 0;

        minX = Math.min(minX, proj.x);
        maxX = Math.max(maxX, proj.x);
        minY = Math.min(minY, proj.y);
        maxY = Math.max(maxY, proj.y);
      }

      const starColor = getSpectralColor(star.teff || star.temp_k);
      color.set(starColor);
      colors[i * 3] = color.r;
      colors[i * 3 + 1] = color.g;
      colors[i * 3 + 2] = color.b;

      const mag = star.magnitude || 15;
      sizes[i] = Math.max(1.5, 6 * Math.pow(10, -0.15 * (mag - magMin)));
    });

    const starGeo = new THREE.BufferGeometry();
    starGeo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    starGeo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    starGeo.setAttribute('size', new THREE.BufferAttribute(sizes, 1));

    const starMat = new THREE.ShaderMaterial({
      uniforms: {},
      vertexShader: `
        attribute float size;
        varying vec3 vColor;
        void main() {
          vColor = color;
          vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
          gl_PointSize = size * (200.0 / -mvPosition.z);
          gl_Position = projectionMatrix * mvPosition;
        }
      `,
      fragmentShader: `
        varying vec3 vColor;
        void main() {
          float r = length(gl_PointCoord - vec2(0.5));
          if (r > 0.5) discard;
          float alpha = 1.0 - smoothstep(0.2, 0.5, r);
          gl_FragColor = vec4(vColor, alpha);
        }
      `,
      vertexColors: true,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending
    });
    
    const starField = new THREE.Points(starGeo, starMat);
    starFieldRef.current = starField;
    scene.add(starField);
    
    return { minX, maxX, minY, maxY };
  }, []);

  // Handle fullscreen change - USE FULL VIEWPORT
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isFS = !!document.fullscreenElement;
      setIsFullscreen(isFS);
      
      if (rendererRef.current && containerRef.current && cameraRef.current) {
        const containerWidth = containerRef.current.clientWidth;
        const containerHeight = containerRef.current.clientHeight;
        
        // In fullscreen: USE FULL VIEWPORT (no 5:1 constraint)
        // In normal mode: use 5:1 aspect ratio
        let rendererWidth, rendererHeight;
        
        if (isFS) {
          // FULLSCREEN: Fill entire viewport
          rendererWidth = containerWidth;
          rendererHeight = containerHeight;
        } else {
          // NORMAL: 5:1 aspect ratio
          const targetAspect = 5 / 1;
          const containerAspect = containerWidth / containerHeight;
          
          if (containerAspect > targetAspect) {
            rendererHeight = containerHeight;
            rendererWidth = rendererHeight * targetAspect;
          } else {
            rendererWidth = containerWidth;
            rendererHeight = rendererWidth / targetAspect;
          }
        }
        
        rendererRef.current.setSize(rendererWidth, rendererHeight);
        
        // Center the canvas
        const canvas = rendererRef.current.domElement;
        canvas.style.position = 'absolute';
        canvas.style.left = `${(containerWidth - rendererWidth) / 2}px`;
        canvas.style.top = `${(containerHeight - rendererHeight) / 2}px`;
        
        // Update camera aspect ratio for fullscreen
        if (cameraRef.current && isFS) {
          const camera = cameraRef.current;
          const currentCenterX = (camera.left + camera.right) / 2;
          const currentCenterY = (camera.top + camera.bottom) / 2;
          const currentHeight = camera.top - camera.bottom;
          const newAspect = rendererWidth / rendererHeight;
          const newWidth = currentHeight * newAspect;
          
          camera.left = currentCenterX - newWidth / 2;
          camera.right = currentCenterX + newWidth / 2;
          camera.updateProjectionMatrix();
        }
        
        if (sceneRef.current) {
          rendererRef.current.render(sceneRef.current, cameraRef.current);
        }
      }
    };
    
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Main Three.js setup
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    if (container.querySelector('canvas')) return;

    // Store raw data for dynamic re-projection
    rawDataRef.current = { stars, constellations, zenithRa, zenithDec };

    // Scene
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x050510);
    sceneRef.current = scene;

    // Calculate initial view bounds
    const raMin = ribbonBounds?.ra_min || (zenithRa - 0.5);
    const raMax = ribbonBounds?.ra_max || (zenithRa + 0.5);
    const decMin = ribbonBounds?.dec_min || (zenithDec - 0.1);
    const decMax = ribbonBounds?.dec_max || (zenithDec + 0.1);

    const raSpanDeg = (raMax - raMin);
    const decSpanDeg = (decMax - decMin);
    const initialFovDeg = Math.max(raSpanDeg, decSpanDeg) * 1.2;
    lastFovRef.current = initialFovDeg;

    // Container dimensions
    const width = container.clientWidth || 800;
    const height = width / 5;

    // Initial camera
    const decSpanArcmin = decSpanDeg * 60;
    const padding = 1.15;
    let viewHeight = decSpanArcmin * padding;
    let viewWidth = viewHeight * (width / height);
    
    const camera = new THREE.OrthographicCamera(
      -viewWidth / 2, viewWidth / 2,
      viewHeight / 2, -viewHeight / 2,
      0.1, 1000
    );
    camera.position.set(0, 0, 100);
    camera.lookAt(0, 0, 0);
    cameraRef.current = camera;

    // Renderer
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    container.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // Controls
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableRotate = false;
    controls.enablePan = true;
    controls.enableZoom = true;
    controls.panSpeed = 0.5;
    controls.zoomSpeed = 0.8;
    controls.minZoom = 0.001;  // Allow extreme zoom out
    controls.maxZoom = 100;
    controls.mouseButtons = {
      LEFT: THREE.MOUSE.PAN,
      MIDDLE: THREE.MOUSE.DOLLY,
      RIGHT: THREE.MOUSE.PAN
    };
    controlsRef.current = controls;

    // Build initial stars
    const bounds = rebuildStars(scene, initialFovDeg, zenithRa, zenithDec, stars);
    
    // Recalculate camera to fit data
    if (bounds.minX !== Infinity) {
      const actualXSpanArcmin = bounds.maxX - bounds.minX;
      const actualYSpanArcmin = bounds.maxY - bounds.minY;
      const centerXArcmin = (bounds.minX + bounds.maxX) / 2;
      const centerYArcmin = (bounds.minY + bounds.maxY) / 2;
      
      const dataPadding = 1.2;
      const finalViewHeight = actualYSpanArcmin * dataPadding;
      const finalViewWidth = finalViewHeight * (width / height);
      
      camera.left = centerXArcmin - finalViewWidth / 2;
      camera.right = centerXArcmin + finalViewWidth / 2;
      camera.top = centerYArcmin + finalViewHeight / 2;
      camera.bottom = centerYArcmin - finalViewHeight / 2;
      camera.updateProjectionMatrix();
    }

    // Build initial constellations
    rebuildConstellations(scene, initialFovDeg, zenithRa, zenithDec, constellations);

    // Re-add overlay mesh if it exists (persists across scene rebuilds)
    if (overlayMeshRef.current) {
      scene.add(overlayMeshRef.current);
    }

    // Grid
    const gridPoints = [];
    const viewSpanX = camera.right - camera.left;
    const viewSpanY = camera.top - camera.bottom;
    const xGridSpacing = Math.pow(10, Math.floor(Math.log10(viewSpanX / 10)));
    const yGridSpacing = Math.pow(10, Math.floor(Math.log10(viewSpanY / 10)));
    
    for (let x = Math.floor(camera.left / xGridSpacing) * xGridSpacing; x <= camera.right; x += xGridSpacing) {
      gridPoints.push(new THREE.Vector3(x, camera.bottom, -1));
      gridPoints.push(new THREE.Vector3(x, camera.top, -1));
    }
    for (let y = Math.floor(camera.bottom / yGridSpacing) * yGridSpacing; y <= camera.top; y += yGridSpacing) {
      gridPoints.push(new THREE.Vector3(camera.left, y, -1));
      gridPoints.push(new THREE.Vector3(camera.right, y, -1));
    }
    
    const gridGeo = new THREE.BufferGeometry().setFromPoints(gridPoints);
    const gridMat = new THREE.LineBasicMaterial({ color: 0x334455, opacity: 0.3, transparent: true });
    scene.add(new THREE.LineSegments(gridGeo, gridMat));

    // Zenith line
    const zenithGeo = new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(0, -50000, 1),
      new THREE.Vector3(0, 50000, 1)
    ]);
    const zenithMat = new THREE.LineBasicMaterial({ color: 0x00ffff });
    scene.add(new THREE.Line(zenithGeo, zenithMat));

    console.log(`Initial FOV: ${initialFovDeg.toFixed(1)}°`);

    // Animation loop with dynamic projection updates
    let animationId;
    const animate = () => {
      animationId = requestAnimationFrame(animate);
      controls.update();
      
      // Calculate current FOV based on camera bounds and zoom
      const currentViewWidth = (camera.right - camera.left) / camera.zoom;
      const newFovDeg = currentViewWidth / 60;  // arcmin to degrees
      
      // Update state for UI
      setCurrentFov(newFovDeg);
      
      // If FOV changed significantly (>10 degrees), re-project everything
      const fovChange = Math.abs(newFovDeg - lastFovRef.current);
      if (fovChange > 10) {
        console.log(`FOV changed: ${lastFovRef.current.toFixed(1)}° → ${newFovDeg.toFixed(1)}° (rebuilding)`);
        lastFovRef.current = newFovDeg;
        
        // Rebuild with new projection
        const { stars: rawStars, constellations: rawConst, zenithRa: ra0, zenithDec: dec0 } = rawDataRef.current;
        rebuildStars(scene, newFovDeg, ra0, dec0, rawStars);
        rebuildConstellations(scene, newFovDeg, ra0, dec0, rawConst);
      }
      
      renderer.render(scene, camera);
    };
    animate();

    return () => {
      cancelAnimationFrame(animationId);
      controls.dispose();
      renderer.dispose();
      const canvas = container.querySelector('canvas');
      if (canvas) container.removeChild(canvas);
    };
  }, [stars, zenithRa, zenithDec, ribbonBounds, constellations, viewMode, rebuildStars, rebuildConstellations]);

  // hips2fits sky survey overlay loading
  useEffect(() => {
    let cancelled = false;

    const scene = sceneRef.current;
    const camera = cameraRef.current;

    // Clean up existing overlay
    if (overlayMeshRef.current && scene) {
      scene.remove(overlayMeshRef.current);
      overlayMeshRef.current.geometry.dispose();
      overlayMeshRef.current.material.dispose();
      overlayMeshRef.current = null;
    }
    if (overlayTextureRef.current) {
      overlayTextureRef.current.dispose();
      overlayTextureRef.current = null;
    }

    if (overlayMode !== 'hips2fits' || !scene || !camera) {
      setOverlayLoading(false);
      return;
    }

    setOverlayLoading(true);

    // --- Precise alignment: derive extent from ribbon bounds, not camera ---
    let raMin = ribbonBounds?.ra_min ?? (zenithRa - 0.5);
    let raMax = ribbonBounds?.ra_max ?? (zenithRa + 0.5);
    const decMin = ribbonBounds?.dec_min ?? (zenithDec - 0.1);
    const decMax = ribbonBounds?.dec_max ?? (zenithDec + 0.1);

    // Handle RA wrap-around (e.g. ra_min=358, ra_max=2)
    if (raMax < raMin) raMax += 360;

    const raSpanDeg  = raMax - raMin;
    const decSpanDeg = decMax - decMin;

    // Center of the ribbon (may differ from zenith if ribbon is asymmetric)
    const imageCenterRa  = ((raMin + raMax) / 2) % 360;
    const imageCenterDec = (decMin + decMax) / 2;

    // 15% padding on each side so the ribbon edges aren't clipped
    const pad = 1.15;
    const overlayWidthDeg  = Math.min(raSpanDeg  * pad, 90);
    const overlayHeightDeg = decSpanDeg * pad;

    // Pixel dimensions: target 512px minimum on the short axis, 4096px max on long axis
    const aspect = overlayWidthDeg / overlayHeightDeg; // typically 5:1–25:1
    let imgH = Math.max(512, Math.round(4096 / aspect));
    let imgW = Math.round(imgH * aspect);
    if (imgW > 4096) { imgW = 4096; imgH = Math.round(imgW / aspect); }
    // Clamp to hips2fits 45M pixel safe limit
    if (imgW * imgH > 45_000_000) {
      imgW = Math.round(Math.sqrt(45_000_000 * aspect));
      imgH = Math.round(imgW / aspect);
    }
    const imgWidth  = Math.max(256, imgW);
    const imgHeight = Math.max(256, imgH);

    const url = `https://alasky.cds.unistra.fr/hips-image-services/hips2fits` +
      `?hips=${encodeURIComponent(surveyId)}` +
      `&width=${imgWidth}&height=${imgHeight}` +
      `&fov=${overlayWidthDeg.toFixed(4)}` +
      `&ra=${imageCenterRa.toFixed(6)}&dec=${imageCenterDec.toFixed(6)}` +
      `&projection=TAN&format=png`;

    // Capture these for the async callback closure
    const capturedCenterRa  = imageCenterRa;
    const capturedCenterDec = imageCenterDec;
    const capturedWidthDeg  = overlayWidthDeg;
    const capturedHeightDeg = overlayHeightDeg;
    const capturedZenithRa  = zenithRa;
    const capturedZenithDec = zenithDec;

    const loader = new THREE.TextureLoader();
    loader.setCrossOrigin('anonymous');
    loader.load(
      url,
      (texture) => {
        if (cancelled) { texture.dispose(); return; }

        overlayTextureRef.current = texture;

        // Flip horizontally: FITS has RA increasing leftward, our scene has it rightward
        texture.wrapS = THREE.RepeatWrapping;
        texture.repeat.x = -1;
        texture.offset.x = 1;

        // Plane sized exactly to the image's angular footprint (degrees → arcminutes)
        const planeWidth  = capturedWidthDeg  * 60;
        const planeHeight = capturedHeightDeg * 60;

        const geometry = new THREE.PlaneGeometry(planeWidth, planeHeight);
        const material = new THREE.MeshBasicMaterial({
          map: texture,
          transparent: true,
          opacity: overlayOpacity, // eslint-disable-line react-hooks/exhaustive-deps
          depthWrite: false,
        });

        const mesh = new THREE.Mesh(geometry, material);

        // Place plane at the ribbon center projected into scene coordinates
        const centerProj = projectGnomonic(capturedCenterRa, capturedCenterDec, capturedZenithRa, capturedZenithDec);
        const planeCx = centerProj?.x ?? 0;
        const planeCy = centerProj?.y ?? 0;
        mesh.position.set(planeCx, planeCy, -0.5);

        overlayMeshRef.current = mesh;
        scene.add(mesh);
        setOverlayLoading(false);
      },
      undefined,
      (error) => {
        if (cancelled) return;
        console.error('Failed to load sky survey overlay:', error);
        setOverlayLoading(false);
      }
    );

    return () => { cancelled = true; };
  }, [overlayMode, surveyId, zenithRa, zenithDec, viewMode, ribbonBounds]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update overlay opacity without reloading texture
  useEffect(() => {
    if (overlayMeshRef.current) {
      overlayMeshRef.current.material.opacity = overlayOpacity;
    }
  }, [overlayOpacity]);

  // Toggle canvas transparency for Aladin mode
  useEffect(() => {
    const scene = sceneRef.current;
    const renderer = rendererRef.current;
    if (!scene || !renderer) return;

    const canvas = renderer.domElement;
    if (overlayMode === 'aladin') {
      scene.background = null;
      renderer.setClearColor(0x000000, 0);
      canvas.style.position = 'relative';
      canvas.style.zIndex = '1';
    } else {
      scene.background = new THREE.Color(0x050510);
      renderer.setClearColor(0x050510, 1);
      canvas.style.zIndex = '';
    }
  }, [overlayMode]);

  if (!stars.length) {
    return (
      <div className="w-full aspect-[5/1] bg-black/50 rounded-xl border border-slate-700 flex items-center justify-center">
        <p className="text-slate-500">No star data available</p>
      </div>
    );
  }

  return (
    <div 
      ref={containerRef}
      className={`relative ${isFullscreen ? 'w-screen h-screen bg-black' : 'w-full'} rounded-xl overflow-hidden border border-cyan-500/30`}
      style={!isFullscreen ? { aspectRatio: '5/1' } : undefined}
    >
      {/* Aladin Lite sky viewer (behind transparent Three.js canvas) */}
      {overlayMode === 'aladin' && (
        <Suspense fallback={
          <div className="absolute inset-0 z-0 flex items-center justify-center bg-black">
            <span className="text-xs font-mono text-yellow-400 animate-pulse">Loading Aladin...</span>
          </div>
        }>
          <AladinOverlay
            ra={zenithRa}
            dec={zenithDec}
            fov={currentFov}
            surveyId={surveyId}
          />
        </Suspense>
      )}

      {/* Controls */}
      <div className="absolute top-2 right-2 z-10 flex gap-2">
        <button
          onClick={() => setShowOverlayPanel(prev => !prev)}
          className={`p-2 border rounded transition-colors ${
            overlayMode !== 'off'
              ? 'bg-indigo-500/30 border-indigo-400 text-indigo-300'
              : 'bg-black/70 hover:bg-black/90 border-cyan-500/30 text-cyan-400'
          }`}
          title="Sky Survey Overlay"
        >
          <Layers className="w-4 h-4" />
        </button>
        <button
          onClick={toggleViewMode}
          className={`p-2 border rounded transition-colors ${
            viewMode === 'fullsky' 
              ? 'bg-cyan-500/30 border-cyan-400 text-cyan-300' 
              : 'bg-black/70 hover:bg-black/90 border-cyan-500/30 text-cyan-400'
          }`}
          title={viewMode === 'fullsky' ? "Birth Ribbon View" : "Full Sky View"}
        >
          {viewMode === 'fullsky' ? <Target className="w-4 h-4" /> : <Globe className="w-4 h-4" />}
        </button>
        <button
          onClick={handleReset}
          className="p-2 bg-black/70 hover:bg-black/90 border border-cyan-500/30 rounded text-cyan-400 transition-colors"
          title="Reset View"
        >
          <RotateCcw className="w-4 h-4" />
        </button>
        <button
          onClick={toggleFullscreen}
          className="p-2 bg-black/70 hover:bg-black/90 border border-cyan-500/30 rounded text-cyan-400 transition-colors"
          title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
        >
          {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
        </button>
      </div>

      {/* Sky Survey Overlay Panel */}
      {showOverlayPanel && (
        <div className="absolute top-12 right-2 z-10 bg-black/80 border border-cyan-500/30 rounded-lg p-3 min-w-[200px] backdrop-blur-sm">
          <div className="text-xs font-mono text-slate-400 mb-2">Sky Survey</div>

          {/* Mode toggle */}
          <div className="flex gap-1 mb-2">
            {['off', 'hips2fits', 'aladin'].map(mode => (
              <button
                key={mode}
                onClick={() => setOverlayMode(mode)}
                className={`px-2 py-1 text-xs font-mono rounded transition-colors ${
                  overlayMode === mode
                    ? 'bg-cyan-500/30 border border-cyan-400 text-cyan-300'
                    : 'bg-black/50 border border-slate-600 text-slate-400 hover:text-slate-300'
                }`}
              >
                {mode === 'off' ? 'Off' : mode === 'hips2fits' ? 'HiPS' : 'Aladin'}
              </button>
            ))}
          </div>

          {/* Survey selector + opacity (when overlay active) */}
          {overlayMode !== 'off' && (
            <>
              <select
                value={surveyId}
                onChange={(e) => setSurveyId(e.target.value)}
                className="w-full bg-black/70 border border-slate-600 rounded px-2 py-1 text-xs font-mono text-cyan-300 mb-2 focus:border-cyan-500 focus:outline-none"
              >
                {SKY_SURVEYS.map(s => (
                  <option key={s.id} value={s.id} title={s.desc}>{s.label}</option>
                ))}
              </select>

              {/* Opacity slider */}
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-mono text-slate-500">Opacity</span>
                <input
                  type="range"
                  min={0}
                  max={1}
                  step={0.05}
                  value={overlayOpacity}
                  onChange={(e) => setOverlayOpacity(parseFloat(e.target.value))}
                  className="flex-1 h-1 accent-cyan-500"
                />
                <span className="text-xs font-mono text-slate-400 w-8">{Math.round(overlayOpacity * 100)}%</span>
              </div>

              {overlayLoading && (
                <div className="text-xs font-mono text-yellow-400 mt-2 animate-pulse">Loading survey...</div>
              )}

              {/* Print Quality Section */}
              {bandId && onGeneratePrintImage && (
                <>
                  <div className="border-t border-slate-600/50 mt-3 pt-3">
                    <div className="text-xs font-mono text-slate-400 mb-2 flex items-center gap-1">
                      <Printer className="w-3 h-3" />
                      Print Quality
                    </div>

                    {/* Preset selector */}
                    <select
                      value={printPreset}
                      onChange={(e) => setPrintPreset(e.target.value)}
                      className="w-full bg-black/70 border border-slate-600 rounded px-2 py-1 text-xs font-mono text-cyan-300 mb-2 focus:border-cyan-500 focus:outline-none"
                    >
                      {PRINT_PRESETS.map(p => (
                        <option key={p.value} value={p.value}>{p.desc} ({p.label})</option>
                      ))}
                    </select>

                    {/* Action buttons */}
                    <div className="flex gap-1 mb-2">
                      <button
                        onClick={() => onGeneratePrintImage(surveyId, printPreset, false)}
                        className="flex-1 px-2 py-1.5 text-[10px] font-mono bg-indigo-600/40 border border-indigo-500/50 rounded text-indigo-200 hover:bg-indigo-600/60 transition-colors"
                      >
                        Save Print Image
                      </button>
                      <button
                        onClick={() => onGeneratePrintImage(surveyId, printPreset, true)}
                        className="flex-1 px-2 py-1.5 text-[10px] font-mono bg-purple-600/40 border border-purple-500/50 rounded text-purple-200 hover:bg-purple-600/60 transition-colors"
                      >
                        Save All 9
                      </button>
                    </div>

                    {/* Per-survey status list */}
                    {Object.keys(printImageStatus).length > 0 && (
                      <div className="space-y-1 max-h-[160px] overflow-y-auto">
                        {Object.entries(printImageStatus).map(([imgId, img]) => (
                          <div key={imgId} className="flex items-center gap-1.5 text-[10px] font-mono">
                            {img.status === 'complete' ? (
                              <Check className="w-3 h-3 text-green-400 flex-shrink-0" />
                            ) : img.status === 'failed' ? (
                              <AlertCircle className="w-3 h-3 text-red-400 flex-shrink-0" />
                            ) : (
                              <Loader2 className="w-3 h-3 text-cyan-400 animate-spin flex-shrink-0" />
                            )}

                            <span className={`truncate ${
                              img.status === 'complete' ? 'text-green-300' :
                              img.status === 'failed' ? 'text-red-300' : 'text-slate-300'
                            }`}>
                              {img.survey_label}
                            </span>

                            {img.status === 'complete' && img.file_size_bytes && (
                              <span className="text-slate-500 flex-shrink-0">
                                ({(img.file_size_bytes / 1024 / 1024).toFixed(1)}MB)
                              </span>
                            )}

                            {img.status !== 'complete' && img.status !== 'failed' && (
                              <span className="text-cyan-400 flex-shrink-0">
                                ({img.progress_pct || 0}%)
                              </span>
                            )}

                            {img.status === 'complete' && img.download_url && (
                              <a
                                href={`${process.env.REACT_APP_BACKEND_URL}${img.download_url}`}
                                className="ml-auto flex-shrink-0 text-cyan-400 hover:text-cyan-300"
                                download
                              >
                                <Download className="w-3 h-3" />
                              </a>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      )}

      {/* Info */}
      <div className="absolute top-2 left-2 z-10 text-xs font-mono hidden sm:block">
        <div className="bg-black/70 border border-cyan-500/30 rounded px-2 py-1">
          <span className="text-cyan-400">{stars.length.toLocaleString()}</span>
          <span className="text-slate-500"> objects</span>
          {constellations?.length > 0 && (
            <>
              <span className="text-slate-500"> • </span>
              <span className="text-green-400">{constellations.length}</span>
              <span className="text-slate-500"> const</span>
            </>
          )}
          <span className="text-slate-500"> • </span>
          <span className="text-yellow-400">{currentFov.toFixed(0)}°</span>
          <span className="text-slate-500"> FOV</span>
        </div>
      </div>

      {/* Legend */}
      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 z-10">
        <div className="bg-black/70 border border-slate-700 rounded px-3 py-1 text-[10px] font-mono text-slate-400 flex items-center gap-3">
          <span>Scroll=Zoom • Drag=Pan</span>
          <span className="text-cyan-400">|</span>
          <span className="text-cyan-400">Zenith</span>
          <span className="text-green-400">|</span>
          <span className="text-green-400">Constellations</span>
        </div>
      </div>
    </div>
  );
}
